// Humanize AI service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (humanize_ai_params)
//   views      app/views/job_postings/steps/humanize_ai/_one.._seven.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (humanize_ai__one.._seven)
//   cost       app/models/job_postings/humanize_ai_posting.rb (#cost / #editing_cost / #image_cost)
//   parity     tasks/post-a-project/parity/writing-variants.md (HUMANIZE_AI)
//
// 7-step shape. fiction / use_ai are RADIOS over boolean values; because the
// engine RadioGroup emits strings, the option values are the boolean string forms
// "true"/"false" (Rails coerces them back to booleans). `isTrue`/`isFalse`
// normalize them in predicates.
//
// Restorations vs the design prototype:
//   - subtitle is optional
//   - real AI consent step (mode "3"); OMITTED when use_ai === true (AI path skips consent)
//   - images is a REQUIRED number (default 0), priced $6/image (validations:1166-1167)
//   - all-tier submitted value = "Allow All to Request"

import {
  FICTION_WRITING_TIERS,
  ALL_TIER_VALUE,
  WRITING_STYLES,
  WRITING_GENRES,
  WRITING_EDITING_STYLES,
  RATES,
} from "../options"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

const FINANCE_AND_TECH = "Finance and Technology"

// Boolean radios round-trip as the strings "true"/"false" through the engine.
const isTrue = (v) => v === true || v === "true"
const isFalse = (v) => v === false || v === "false"

// editing add — Ruby #editing_cost = (word_count / 100) * rate [integer floor].
// Server-traced via ctx.rates (ruling C2 / tuw-app:P-001), RATES seed as fallback.
const editingRateFor = (style, ctx = {}) =>
  ctx.rates?.writing_editing_styles_cost?.[style] ?? RATES.WRITING_EDITING_STYLES_COST[style] ?? 0

// Per-100-word writer rate for one tier (use_ai swaps the rate family). Reads the
// server-resolved, Insider-aware ctx.rates map per-key: a null server leaf for
// that tier falls back to the RATES seed for THAT tier, never to 0 (tuw-app:P-001).
// Returns undefined for a tier absent from both maps so `!= null` guards hold.
const writerRateFor = (tier, useAi, ctx = {}) =>
  useAi
    ? (ctx.rates?.humanize_ai_with_ai_writer_cost?.[tier] ?? RATES.HUMANIZE_AI_WITH_AI_WRITER_COST[tier])
    : (ctx.rates?.humanize_ai_writer_cost?.[tier] ?? RATES.HUMANIZE_AI_WRITER_COST[tier])

const rateText = (tier, useAi, ctx = {}) => {
  const rate = writerRateFor(tier, useAi, ctx)
  return rate != null ? `$${rate.toFixed(2)} / 100 words` : undefined
}

// Concrete (non-All) tiers, used for the All-Tier "From" floor price.
const CONCRETE_TIERS = ["Premium", "Top", "Urban", "Rising"]

// All-Tier "From $X / 100 words" = the minimum concrete-tier rate for the active
// humanization method (design tierOpts `From` idiom). Server-traced.
const allTierFromText = (useAi, ctx = {}) => {
  const rates = CONCRETE_TIERS.map((t) => writerRateFor(t, useAi, ctx)).filter((r) => r != null)
  if (!rates.length) return undefined
  return `From $${Math.min(...rates).toFixed(2)} / 100 words`
}

// Design tier descriptions (service-config.js TIER_DESCS + All-tier copy). Em dash
// converted per the no-em-dash rule.
const TIER_DESCRIPTIONS = {
  [ALL_TIER_VALUE]:
    "Open the project to writers of every tier, you pay the rate of whoever you attach.",
  Premium: "Our highest-performing writers.",
  Top: "Proven, consistent writers.",
  Urban: "Up-and-coming writers with potential.",
  Rising: "Vetted writers just getting started.",
}

// Design W_EDIT option descriptions (service-config.js). Em dash converted.
const WRITING_EDITING_DESCRIPTIONS = {
  "Essential Editing":
    "Grammar, spelling, syntax, punctuation and inconsistencies, included with every writing package.",
  "Line & Essential Editing":
    "Everything in Essential, plus a line-by-line review for clarity, flow, phrasing and sentence variety.",
}

// Per-image add-on rate (humanize_ai_image_cost), server-traced.
const imageRate = (ctx = {}) => ctx.rates?.humanize_ai_image_cost ?? RATES.HUMANIZE_AI_IMAGE_COST

export const humanizeAi = {
  key: "humanize_ai",
  postingKey: "humanize_ai_posting",
  name: "Humanize AI",

  steps: (values = {}, ctx = {}) => {
    const useAi = isTrue(values.use_ai)
    const finTech = values.tier === FINANCE_AND_TECH

    return [
      // Step 1 — Fiction or non-fiction (humanize_ai __one).
      {
        id: "format",
        title: "Fiction or non-fiction?",
        sub: "Tells us which writers to match, and how your content gets humanized.",
        fields: [
          {
            type: "radio",
            key: "fiction",
            label: "What are you humanizing?",
            required: true,
            options: [
              { value: "true", label: "Fiction" },
              { value: "false", label: "Non-fiction" },
            ],
          },
        ],
      },

      // Step 2 — How humanized (humanize_ai __two). Skipped for fiction OR when the
      // customer lacks the AI feature (job_posting.rb:819-822).
      {
        id: "method",
        title: "How should we humanize it?",
        sub: "Two ways to turn AI-generated content into a manuscript that reads like a human wrote it.",
        when: (v, ctx) => isFalse(v.fiction) && !!(ctx && ctx.canUseAi),
        fields: [
          {
            type: "radio",
            key: "use_ai",
            label: "Humanization method",
            required: true,
            options: [
              {
                value: "false",
                label: "Humanization without AI",
                sub: "A writer and editor humanize your content entirely by hand.",
              },
              {
                value: "true",
                label: "Humanization with AI",
                sub: "A writer pairs AI tools with a human edit for a faster turnaround.",
              },
            ],
          },
        ],
      },

      // Step 3 — Manuscript upload (humanize_ai __three; file REQUIRED).
      {
        id: "manuscript",
        title: "Manuscript upload",
        sub: "Upload the AI-generated content you want humanized.",
        fields: [
          {
            type: "file",
            key: "file",
            label: "AI content to humanize",
            required: true,
            accept: ".doc,.docx,.pdf,.zip",
            placeholder: "Upload your manuscript",
            help: "Supported: .doc, .docx, .pdf, .zip (max 24MB).",
          },
        ],
      },

      // Step 4 — Writer tier (humanize_ai __four; FICTION_WRITING_TIERS). All-Tier
      // submits the backend value "Allow All to Request" (registered options
      // exception); prices + descriptions match the design.
      {
        id: "tier",
        title: "Writer tier",
        sub: "Higher tiers bring more experience. Pricing is per 100 words and reflects your humanization method.",
        fields: [
          {
            type: "radio",
            key: "tier",
            required: true,
            options: FICTION_WRITING_TIERS.map((tier) => ({
              value: tier,
              label: tier === ALL_TIER_VALUE ? "All Tier Access" : tier,
              price: tier === ALL_TIER_VALUE ? allTierFromText(useAi, ctx) : rateText(tier, useAi, ctx),
              sub: TIER_DESCRIPTIONS[tier],
            })),
          },
        ],
      },

      // Step 5 — Document details (humanize_ai __five).
      {
        id: "details",
        title: "Document details",
        sub: "Tell your writer about the book. Includes eBook formatting + a plagiarism scan. Minimum 1,000 words.",
        fields: [
          {
            type: "wordcount",
            key: "word_count",
            label: "Word count",
            required: true,
            min: 1000,
            step: 500,
          },
          {
            type: "select",
            key: "writing_style",
            label: "Writing style",
            required: true,
            blankLabel: "Select writing style",
            options: asOptions(WRITING_STYLES),
            when: (v) => isTrue(v.fiction),
          },
          {
            type: "select",
            key: "genre",
            label: "Genre",
            required: true,
            blankLabel: "Select Genre",
            options: asOptions([...WRITING_GENRES].sort()),
            when: (v) => isTrue(v.fiction),
          },
          {
            type: "select",
            key: "niche",
            label: "Niche / genre",
            required: true,
            blankLabel: "Select niche/genre",
            options: (v = {}, ctx = {}) =>
              asOptions((ctx.options && ctx.options.nichesForTier(v.tier)) || []),
            when: (v) => isFalse(v.fiction),
          },
          { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
          { type: "text", key: "subtitle", label: "Subtitle", required: false },
          { type: "text", key: "author_name", label: "Author name", required: true, placeholder: "As it should appear" },
          {
            type: "textarea",
            key: "author_bio",
            label: "Author bio",
            required: false,
            rows: 3,
            when: (v) => isFalse(v.fiction),
          },
          {
            type: "toggle",
            key: "nsfw",
            label: "Topic is not safe for work",
            detailsKey: "nsfw_details",
            detailsLabel: "Reason topic is not safe for work",
            detailsType: "textarea",
            detailsRequired: true,
            when: (v) => !finTech || isTrue(v.nsfw),
          },
          {
            type: "triggerWarning",
            label: "This book contains sensitive or triggering content",
            help: "Flag anything a writer should be aware of before taking the project.",
            when: (v) => !finTech || isTrue(v.trigger_warning),
          },
          {
            type: "number",
            key: "images",
            // Price in the label derives from the same server-resolved rate the
            // help + rail use (imageRate(ctx)), never a hardcoded literal, so the
            // label can't drift from the billed amount (tuw-app:P-001).
            label: `Add images ($${imageRate(ctx)} per image)`,
            required: true,
            min: 0,
            max: 100,
            step: 1,
            help: `$${imageRate(ctx)} per image. Enter 0 for none.`,
          },
        ],
      },

      // Step 6 — Editing style (humanize_ai __six).
      {
        id: "editing",
        title: "Editing style",
        sub: "Editing is included with every writing package. Choose your level.",
        fields: [
          {
            type: "radio",
            key: "editing_style",
            required: true,
            options: (v = {}, ctx = {}) =>
              WRITING_EDITING_STYLES.map((style) => {
                const rate = editingRateFor(style, ctx)
                return {
                  value: style,
                  label: style,
                  price: rate > 0 ? `$${(rate * 10).toFixed(2)} / 1,000 words` : "Included",
                  sub: WRITING_EDITING_DESCRIPTIONS[style],
                }
              }),
          },
        ],
      },

      // Step 7 — AI consent (humanize_ai __seven; mode "3"). OMITTED only on the
      // non-fiction AI path. Fiction is always human, so consent must show even if
      // a stale use_ai lingers from a prior non-fiction pick (Plumbline bug 4) —
      // the fiction-aware guard prevents the consent step being wrongly hidden
      // (and the resulting submit 422 / silent no-consent).
      {
        id: "consent",
        title: "AI use in your project",
        sub: "Choose how much your writer may use AI. Please select one.",
        when: (v) => isTrue(v.fiction) || !isTrue(v.use_ai),
        fields: [
          {
            type: "consent",
            key: "ai_consent",
            mode: "3",
            label: "AI use in your project",
            required: true,
          },
        ],
      },
    ]
  },

  // Instant estimate for the PriceRail. Ruby humanize_ai #cost (minus the shared
  // QA/QC/managed add-on and the post-scan plagiarized_cost):
  //   (word_count / 100.0) * writer_rate    [exact float]
  //   + (word_count / 100)  * editing_rate  [integer floor]
  //   + 6 * images
  // All-tier shows a "from" range (Rising..Premium). Writer table swaps on use_ai.
  estimate: (values = {}, ctx = {}) => {
    const empty = { lines: [], total: 0, isRange: false, framing: "free-to-post" }

    const wc = Number(values.word_count) || 0
    const tier = values.tier
    if (!tier || wc <= 0) return empty

    const useAi = isTrue(values.use_ai)
    const allTier = tier === ALL_TIER_VALUE
    const rateTier = allTier ? "Rising" : tier
    const writerRate = writerRateFor(rateTier, useAi, ctx)
    if (writerRate == null) return empty

    const images = Number(values.images) || 0
    const writerRaw = (wc / 100) * writerRate
    const editingRaw = Math.floor(wc / 100) * editingRateFor(values.editing_style, ctx)
    const imageRaw = images * imageRate(ctx)

    const lines = [
      {
        label: allTier
          ? `Humanize AI (${wc.toLocaleString()} words, all tiers)`
          : `Humanize AI (${wc.toLocaleString()} words, ${tier})`,
        amount: money(writerRaw),
      },
    ]

    if (editingRaw > 0) {
      lines.push({ label: values.editing_style, amount: money(editingRaw) })
    }

    if (imageRaw > 0) {
      lines.push({ label: `Images (${images})`, amount: money(imageRaw) })
    }

    return {
      lines,
      total: money(writerRaw + editingRaw + imageRaw),
      isRange: allTier,
      fromLabel: "Starting at",
      framing: "free-to-post",
    }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // humanize_ai_posting = [tier, editing_style, word_count, quality_assurance,
  // quality_control, use_ai, images]. images is priced $6/ea by the server
  // calculator (HumanizeAiPosting#image_cost), matching the real order-time #cost.
  // QA/QC are job-level (merged by the engine via addon.addonPriceFields). Null
  // until tier + word count set.
  pricePayload: (values = {}) => {
    const wc = Number(values.word_count) || 0
    if (!values.tier || wc <= 0) return null

    return {
      postingType: "humanize_ai_posting",
      tier: values.tier,
      editingStyle: values.editing_style ?? null,
      wordCount: wc,
      useAi: isTrue(values.use_ai),
      images: Number(values.images) || 0,
    }
  },
}
